export const DOCUMENT_ZIP_POLL_INTERVAL_MS = 2000;
// ZIPs can legitimately take longer than two hours. The server owns the task
// lifetime, so the browser keeps monitoring until the server reports a
// terminal state (or this page stops monitoring).
export const DOCUMENT_ZIP_POLL_TIMEOUT_MS = Number.POSITIVE_INFINITY;
export const DOCUMENT_ZIP_STATUS_REQUEST_TIMEOUT_MS = 30000;
export const DOCUMENT_ZIP_MAX_CONSECUTIVE_FAILURES = 5;
export const DOCUMENT_ZIP_RECEIPT_STORAGE_PREFIX = 'casedb:document-zip:v1';

const DOCUMENT_ZIP_TASK_PATTERN = /^zip_(?:[a-f0-9]{32}|[a-f0-9]{13,14}\.[0-9]{8})$/i;
const DOCUMENT_ZIP_RECEIPT_VERSION = 1;

const TERMINAL_FAILURE_STATUSES = new Set(['error', 'failed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);
const TERMINAL_HTTP_STATUSES = new Set([400, 401, 403, 404, 410, 422]);

const retryMessage = (message, fallback) => {
  const detail = String(message || fallback || 'ZIP creation failed.').trim();
  return /\bretry\b/i.test(detail) ? detail : `${detail} Retry the ZIP request.`;
};

const normalizedScopeId = value => {
  const normalized = String(value ?? '').trim();
  return /^\d+$/.test(normalized) && Number(normalized) > 0 ? normalized : '';
};

const receiptStorage = storage => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
};

export const isDocumentZipTaskId = taskId => DOCUMENT_ZIP_TASK_PATTERN.test(
  String(taskId || '').trim()
);

export const getDocumentZipReceiptStorageKey = ({ caseId, userId } = {}) => {
  const normalizedCaseId = normalizedScopeId(caseId);
  const normalizedUserId = normalizedScopeId(userId);
  if (!normalizedCaseId || !normalizedUserId) return '';
  return `${DOCUMENT_ZIP_RECEIPT_STORAGE_PREFIX}:${normalizedUserId}:${normalizedCaseId}`;
};

export const createDocumentZipReceipt = ({
  taskId,
  caseId,
  userId,
  trayId,
  itemCount = 0,
  createdAt = Date.now(),
} = {}) => {
  const normalizedTaskId = String(taskId || '').trim();
  const normalizedCaseId = normalizedScopeId(caseId);
  const normalizedUserId = normalizedScopeId(userId);
  const normalizedTrayId = String(trayId || '').trim();
  if (!isDocumentZipTaskId(normalizedTaskId)
      || !normalizedCaseId
      || !normalizedUserId
      || !/^zip-[A-Za-z0-9_.-]{1,80}$/.test(normalizedTrayId)) {
    return null;
  }

  return {
    version: DOCUMENT_ZIP_RECEIPT_VERSION,
    taskId: normalizedTaskId,
    caseId: normalizedCaseId,
    userId: normalizedUserId,
    trayId: normalizedTrayId,
    itemCount: Math.max(0, Math.min(1000000, Number.parseInt(itemCount, 10) || 0)),
    createdAt: Math.max(0, Number(createdAt) || Date.now()),
  };
};

export const saveDocumentZipReceipt = (receipt, storage = null) => {
  const normalized = createDocumentZipReceipt(receipt);
  const key = getDocumentZipReceiptStorageKey(normalized || {});
  const target = receiptStorage(storage);
  if (!normalized || !key || !target) return null;
  try {
    // Persist only this explicit allowlist. In particular, never persist a
    // share token, signed URL, selected document keys, or cache grant.
    target.setItem(key, JSON.stringify(normalized));
    return normalized;
  } catch (_) {
    return null;
  }
};

export const readDocumentZipReceipt = ({ caseId, userId } = {}, storage = null) => {
  const key = getDocumentZipReceiptStorageKey({ caseId, userId });
  const target = receiptStorage(storage);
  if (!key || !target) return null;
  try {
    const parsed = JSON.parse(target.getItem(key) || 'null');
    if (Number(parsed?.version) !== DOCUMENT_ZIP_RECEIPT_VERSION) {
      target.removeItem(key);
      return null;
    }
    const normalized = createDocumentZipReceipt(parsed || {});
    if (!normalized
        || normalized.caseId !== normalizedScopeId(caseId)
        || normalized.userId !== normalizedScopeId(userId)) {
      target.removeItem(key);
      return null;
    }
    return normalized;
  } catch (_) {
    try { target.removeItem(key); } catch (_) {}
    return null;
  }
};

export const clearDocumentZipReceipt = ({ caseId, userId } = {}, storage = null) => {
  const key = getDocumentZipReceiptStorageKey({ caseId, userId });
  const target = receiptStorage(storage);
  if (!key || !target) return false;
  try {
    target.removeItem(key);
    return true;
  } catch (_) {
    return false;
  }
};

export class DocumentZipPollingError extends Error {
  constructor(message, code, { retryable = true } = {}) {
    super(message);
    this.name = 'DocumentZipPollingError';
    this.code = code;
    this.retryable = retryable;
  }
}

export const preflightDocumentZipDownload = async ({
  downloadUrl,
  fetchImpl = fetch,
  signal = null,
} = {}) => {
  const normalizedUrl = String(downloadUrl || '').trim();
  if (!normalizedUrl) {
    throw new DocumentZipPollingError(
      'The ZIP finished, but its download address is unavailable.',
      'download_not_ready',
    );
  }

  let response;
  try {
    response = await fetchImpl(normalizedUrl, {
      method: 'HEAD',
      credentials: 'include',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) {
      throw new DocumentZipPollingError(
        'ZIP download monitoring stopped.',
        'aborted',
        { retryable: false },
      );
    }
    throw new DocumentZipPollingError(
      'CaseDB could not confirm that the ZIP download is ready.',
      'download_not_ready',
    );
  }

  if (response.ok) return normalizedUrl;

  const status = Number(response.status || 0);
  if ([401, 403].includes(status)) {
    throw new DocumentZipPollingError(
      'You no longer have access to this ZIP download.',
      'download_access_denied',
      { retryable: false },
    );
  }
  if ([404, 410].includes(status)) {
    throw new DocumentZipPollingError(
      'This ZIP download is no longer available.',
      'download_unavailable',
      { retryable: false },
    );
  }
  if ([409, 425, 429].includes(status) || status >= 500 || status === 0) {
    throw new DocumentZipPollingError(
      'The ZIP download is still being prepared.',
      'download_not_ready',
    );
  }
  throw new DocumentZipPollingError(
    `CaseDB could not open the ZIP download (HTTP ${status}).`,
    'download_unavailable',
    { retryable: false },
  );
};

const parseStatusResponse = async (response) => {
  try {
    const result = await response.json();
    return result && typeof result === 'object' ? result : {};
  } catch (_) {
    return {};
  }
};

export const pollDocumentZipStatus = ({
  taskId,
  statusUrl,
  fetchImpl = fetch,
  onStatus = () => {},
  signal = null,
  pollIntervalMs = DOCUMENT_ZIP_POLL_INTERVAL_MS,
  timeoutMs = DOCUMENT_ZIP_POLL_TIMEOUT_MS,
  requestTimeoutMs = DOCUMENT_ZIP_STATUS_REQUEST_TIMEOUT_MS,
  maxConsecutiveFailures = DOCUMENT_ZIP_MAX_CONSECUTIVE_FAILURES,
  now = () => Date.now(),
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  createAbortController = () => new AbortController(),
}) => {
  const normalizedTaskId = String(taskId || '').trim();
  if (!isDocumentZipTaskId(normalizedTaskId)) {
    return Promise.reject(new DocumentZipPollingError(
      'CaseDB did not return a valid ZIP task. Retry the ZIP request.',
      'invalid_task',
    ));
  }

  const endpoint = String(statusUrl || '').trim();
  if (!endpoint) {
    return Promise.reject(new DocumentZipPollingError(
      'CaseDB did not return a ZIP status address. Retry the ZIP request.',
      'invalid_status_url',
    ));
  }

  return new Promise((resolve, reject) => {
    const startedAt = now();
    let pollTimer = null;
    let requestTimer = null;
    let requestController = null;
    let settled = false;
    let consecutiveFailures = 0;

    const cleanupRequest = () => {
      if (requestTimer !== null) {
        clearTimeoutImpl(requestTimer);
        requestTimer = null;
      }
      requestController = null;
    };

    const cleanup = () => {
      if (pollTimer !== null) {
        clearTimeoutImpl(pollTimer);
        pollTimer = null;
      }
      if (requestTimer !== null) {
        clearTimeoutImpl(requestTimer);
        requestTimer = null;
      }
      if (requestController) {
        try { requestController.abort(); } catch (_) {}
        requestController = null;
      }
      signal?.removeEventListener?.('abort', handleExternalAbort);
    };

    const finish = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const handleExternalAbort = () => fail(new DocumentZipPollingError(
      'ZIP status monitoring stopped.',
      'aborted',
      { retryable: false },
    ));

    const elapsed = () => Math.max(0, now() - startedAt);
    const hasElapsedDeadline = Number.isFinite(timeoutMs) && timeoutMs > 0;
    const failForTimeout = () => fail(new DocumentZipPollingError(
      'The ZIP is still running. Reopen this case to continue monitoring the same task.',
      'timeout',
      { retryable: false },
    ));

    const scheduleNext = (poll) => {
      if (!hasElapsedDeadline) {
        pollTimer = setTimeoutImpl(poll, pollIntervalMs);
        return;
      }
      const remaining = timeoutMs - elapsed();
      if (remaining <= 0) {
        failForTimeout();
        return;
      }
      pollTimer = setTimeoutImpl(poll, Math.min(pollIntervalMs, remaining));
    };

    const failOrRetry = (poll, message, code) => {
      consecutiveFailures += 1;
      if (consecutiveFailures >= Math.max(1, maxConsecutiveFailures)) {
        fail(new DocumentZipPollingError(
          retryMessage(message, 'CaseDB could not check ZIP status.'),
          code,
        ));
        return;
      }
      scheduleNext(poll);
    };

    const poll = async () => {
      pollTimer = null;
      if (settled) return;
      if (signal?.aborted) {
        handleExternalAbort();
        return;
      }
      if (hasElapsedDeadline && elapsed() >= timeoutMs) {
        failForTimeout();
        return;
      }

      requestController = createAbortController();
      const currentController = requestController;
      const remaining = hasElapsedDeadline
        ? Math.max(1, timeoutMs - elapsed())
        : requestTimeoutMs;
      requestTimer = setTimeoutImpl(
        () => currentController.abort(),
        Math.min(requestTimeoutMs, remaining),
      );

      try {
        const response = await fetchImpl(endpoint, {
          credentials: 'include',
          signal: currentController.signal,
        });
        const statusData = await parseStatusResponse(response);
        cleanupRequest();
        if (settled) return;

        const status = String(statusData.status || '').toLowerCase();
        if (CANCELLED_STATUSES.has(status)) {
          finish({ ...statusData, status: 'cancelled' });
          return;
        }
        if (TERMINAL_FAILURE_STATUSES.has(status)) {
          fail(new DocumentZipPollingError(
            retryMessage(statusData.message, 'ZIP creation failed.'),
            'terminal_failure',
          ));
          return;
        }
        if (!response.ok || statusData.success !== true) {
          const message = statusData.message
            || `CaseDB could not check ZIP status (HTTP ${response.status || 'error'}).`;
          if (TERMINAL_HTTP_STATUSES.has(Number(response.status))) {
            fail(new DocumentZipPollingError(
              retryMessage(message, 'CaseDB could not check ZIP status.'),
              'terminal_status_response',
            ));
            return;
          }
          failOrRetry(poll, message, 'status_unavailable');
          return;
        }

        consecutiveFailures = 0;
        onStatus(statusData);
        if (status === 'complete' && String(statusData.download_url || '').trim()) {
          finish(statusData);
          return;
        }
        scheduleNext(poll);
      } catch (error) {
        cleanupRequest();
        if (settled) return;
        if (signal?.aborted) {
          handleExternalAbort();
          return;
        }
        const requestTimedOut = error?.name === 'AbortError';
        failOrRetry(
          poll,
          requestTimedOut
            ? 'The ZIP status request timed out.'
            : (error?.message || 'CaseDB could not check ZIP status.'),
          requestTimedOut ? 'request_timeout' : 'status_unavailable',
        );
      }
    };

    if (signal?.aborted) {
      handleExternalAbort();
      return;
    }
    signal?.addEventListener?.('abort', handleExternalAbort, { once: true });
    poll();
  });
};
